/**
 * Authenticated API service for handling image requests
 */
import { apiClient } from './api';

class AuthenticatedImageAPI {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  /**
   * Build API URL for image requests
   * @param {string} imagePath - Path to the image
   * @param {string} size - Image size
   * @returns {string} Complete API URL
   */
  buildImageUrl(imagePath, size = 'original') {
    if (!imagePath) return null;

    // Remove leading slash and normalize path
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    
    // For feedback images that already have size in filename, use the path directly
    // The API response already provides the correct sized URLs (small_image_url, medium_image_url, etc.)
    if (cleanPath.includes('feedback-images')) {
      // Just wrap the path with the assets/image endpoint
      return `assets/image/${cleanPath}`;
    }
    
    // Extract folder and filename from the path for other images
    const lastSlashIndex = cleanPath.lastIndexOf('/');
    const folder = cleanPath.substring(0, lastSlashIndex);
    const filename = cleanPath.substring(lastSlashIndex + 1);
    
    // For other images (like profile images), remove existing size suffix
    const baseFilename = filename.replace(/_(?:original|64x64|100x100|200x200|400x400)\.(jpg|jpeg|png|gif)$/i, '.$1');
    
    // Build the API URL using the assets endpoint
    return `assets/image/${folder}/${baseFilename}/${size}`;
  }

  /**
   * Fetch image with authentication and caching
   * @param {string} imagePath - Path to the image
   * @param {string} size - Image size
   * @returns {Promise<string>} Object URL or cached URL
   */
  async fetchImage(imagePath, size = 'medium') {
    if (!imagePath) return null;

    // Create cache key
    const cacheKey = `${imagePath}_${size}`;
    
    // Return cached version if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if already in progress
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const requestPromise = this._performImageRequest(imagePath, size, cacheKey);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      this.cache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error(`Failed to fetch image ${imagePath}:`, error);
      return this.getFallbackImage(imagePath);
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Perform the actual image request using apiClient
   * @private
   */
  async _performImageRequest(imagePath, size, cacheKey) {
    const imageUrl = this.buildImageUrl(imagePath, this._mapSizeToAPI(size));
    
    if (!imageUrl) {
      throw new Error('Could not build image URL');
    }

    try {
      const response = await apiClient.get(imageUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });

      const blob = response.data;
      const objectUrl = URL.createObjectURL(blob);

      // Store cleanup function for later
      this._scheduleCleanup(cacheKey, objectUrl);

      return objectUrl;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied');
      } else if (error.response?.status === 404) {
        throw new Error('Image not found');
      } else {
        throw new Error(`API Error: ${error.message}`);
      }
    }
  }

  /**
   * Map frontend size names to API size parameters
   * @private
   */
  _mapSizeToAPI(size) {
    const sizeMap = {
      'small': '100x100',
      'medium': '200x200',
      'large': '400x400',
      'original': 'original'
    };
    return sizeMap[size] || '200x200';
  }

  /**
   * Get appropriate fallback image based on image path
   * @private
   */
  getFallbackImage(imagePath) {
    if (imagePath.includes('profile-images')) {
      return '/images/default-avatar.svg';
    } else if (imagePath.includes('institute-logos')) {
      return '/images/default-institute-logo.svg';
    } else if (imagePath.includes('feedback-images')) {
      return '/images/image-placeholder.svg';
    } else {
      return '/images/default-image.svg';
    }
  }

  /**
   * Schedule cleanup of object URLs to prevent memory leaks
   * @private
   */
  _scheduleCleanup(cacheKey, objectUrl, delay = 300000) { // 5 minutes
    setTimeout(() => {
      if (this.cache.get(cacheKey) === objectUrl) {
        URL.revokeObjectURL(objectUrl);
        this.cache.delete(cacheKey);
      }
    }, delay);
  }

  /**
   * Clear all cached images
   */
  clearCache() {
    for (const objectUrl of this.cache.values()) {
      if (objectUrl.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl);
      }
    }
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Preload multiple images
   * @param {Array} imagePaths - Array of image paths to preload
   * @param {string} size - Size to preload
   * @returns {Promise} Promise that resolves when all images are loaded
   */
  async preloadImages(imagePaths, size = 'medium') {
    const promises = imagePaths.map(path => 
      this.fetchImage(path, size).catch(error => {
        console.warn(`Failed to preload image ${path}:`, error);
        return null;
      })
    );

    return Promise.allSettled(promises);
  }
}

// Create singleton instance
const authenticatedImageAPI = new AuthenticatedImageAPI();

export default authenticatedImageAPI;
